// Online play: a shared room, one game state, everybody watching it.
//
// Ported from incan-gold, which took it from no-thanks, which took it from
// thunderstone: client-authoritative full-snapshot sync over Firebase RTDB,
// every move through the same engine.dispatch() as a local game.
//
// WHAT IS DIFFERENT HERE: For Sale needs BOTH multiplayer shapes its siblings
// worked out separately. The buying phase is turn-based - one seat may act
// and `meta.turnSeat` names it, exactly no-thanks's shape. The selling phase
// is simultaneous - every player writes a secret pick and the driver resolves
// when the last one lands, exactly incan-gold's shape. seatsIOwe and
// iNeedToDecide below are therefore phase-aware, and the transaction strategy
// follows the harder case (see commitAction).
//
// A NOTE ON SECRECY, same as the siblings: the whole game state lives in one
// JSON blob every client in the room can read, so a player with devtools open
// could read the shuffled decks, everyone's money, and the picks before the
// reveal. Not fixable without a server. These are games for a family who
// trust each other; the honest description is "hidden from the interface",
// not "cryptographically secret".

import { deadAfterFor, waitingPatch } from './asyncPlay.js';
import {
  ref as dbRef, onValue, onDisconnect, push, set, get, update, remove, runTransaction,
} from 'firebase/database';
import { db } from '../firebase';
import { generateRoomCode, normalizeRoomCode } from '../utils/roomCodes';
import { createGameState } from '../engine/state/createGameState';
import { PHASES, MIN_PLAYERS, MAX_PLAYERS } from '../engine/constants';
import { PERSONALITIES, personalityFor } from '../engine/ai/heuristicBot';

export const GAMES = 'forSaleGames';
export const CODES = 'forSaleGameCodes';
export const USER_GAMES = 'forSaleUserGames';

/**
 * How long a room with nobody in it may sit before it is cleaned up.
 * Same numbers and reasoning as the siblings.
 */
export const DEAD_AFTER_MS = {
  lobby: 60 * 60 * 1000,
  'in-progress': 24 * 60 * 60 * 1000,
  finished: 2 * 60 * 60 * 1000,
};

/**
 * How long a player can be gone before the table stops waiting for them.
 *
 * Matt's standing call from incan-gold: no countdown clock on anyone's
 * decision, but one closed laptop must not hold five people hostage. After
 * this the driver acts for them - in the buying phase they PASS (which takes
 * the cheapest house and half-refunds their bid, the gentlest legal exit),
 * and in the selling phase the steady bot picks for them (there is no "safe"
 * pick to default to - every hand must play a card every round).
 */
export const DISCONNECT_GRACE_MS = 30000;

/** Best-effort "when was anyone last here", tolerant of older rooms. */
export const lastActiveOf = (meta) => {
  if (Number.isFinite(meta?.lastActiveAt)) return meta.lastActiveAt;
  const parsed = Date.parse(meta?.createdAt || '');
  return Number.isFinite(parsed) ? parsed : 0;
};

/** Two conditions, both required. Anyone connected always saves a room. */
export const isDeadRoom = (meta, presence, now = Date.now()) => {
  if (!meta) return false;
  if (Object.keys(presence || {}).length > 0) return false;
  const age = now - lastActiveOf(meta);
  // An async room (asyncPlay.js) is meant to sit for days; it gets the long limits.
  const limit = deadAfterFor(meta, DEAD_AFTER_MS);
  return age > limit;
};

export const clampTableSize = (n) => Math.min(Math.max(Number(n) || MIN_PLAYERS, MIN_PLAYERS), MAX_PLAYERS);

/**
 * The state is stored as a JSON STRING, not as a tree.
 *
 * RTDB has no representation for an empty array or object and silently drops
 * the key. `market: []`, `properties: []` and `cheques: []` are all empty at
 * predictable moments, so a tree write loses them and the state comes back
 * subtly broken. Stringifying keeps it byte-identical. Same as every sibling.
 */
const encode = (state) => JSON.stringify(state);
const decode = (raw) => (raw ? JSON.parse(raw) : null);

export const seqOf = (state) => state?.meta?.seq ?? 0;

/**
 * Who is allowed to write on behalf of the table.
 *
 * The connected human in the lowest seat drives. Derived purely from state
 * every client already has, so they all agree without negotiating, and it
 * survives the host closing their laptop. Two clients briefly disagreeing is
 * harmless: both write the same decision for the same seat, and the second
 * finds it already made and does nothing.
 */
export const driverUidFor = (gameState, presence) => {
  const connected = (uid) => Boolean(presence?.[uid]);
  const humans = (gameState?.players || []).filter((p) => !p.isBot && connected(p.id));
  if (humans.length === 0) return null;
  return humans.reduce((best, p) => (p.seat < best.seat ? p : best)).id;
};

/**
 * Is this seat one that nobody is going to act for themselves?
 *
 * True for a bot, and for a human whose connection has been gone longer than
 * the grace period. False for a connected human - including you.
 */
export const seatIsUnattended = (player, presence, disconnectedSince, asyncRoom = false) => {
  if (player.isBot) return true;
  // Async play (asyncPlay.js): a human who is not here is not here yet. The game waits.
  if (asyncRoom) return false;
  if (presence?.[player.id]) return false;
  const since = disconnectedSince?.[player.id];
  return Boolean(since) && Date.now() - since >= DISCONNECT_GRACE_MS;
};

/**
 * Which seats THIS client owes an action for right now. Phase-aware:
 *
 * - Buying: at most one seat can act - the one `meta.turnSeat` names - so
 *   the answer is that seat or nobody.
 * - Selling: every unattended seat that has not picked yet.
 *
 * The two halves stay separate questions (unattended AND I am the driver),
 * the lesson no-thanks's first online game taught: a single "am I
 * responsible" predicate is true for your own seat too, and reading any true
 * as licence to play automatically bot-plays the humans.
 */
export const seatsIOwe = ({ gameState, presence, myUid, disconnectedSince, asyncRoom = false }) => {
  if (!gameState || gameState.meta.phase === PHASES.FINISHED || gameState.meta.roundOver) return [];
  if (driverUidFor(gameState, presence) !== myUid) return [];

  if (gameState.meta.phase === PHASES.BUYING) {
    const turnPlayer = gameState.players[gameState.meta.turnSeat];
    return turnPlayer && seatIsUnattended(turnPlayer, presence, disconnectedSince, asyncRoom)
      ? [turnPlayer]
      : [];
  }

  return gameState.players.filter((p) => (
    p.pick === null && seatIsUnattended(p, presence, disconnectedSince, asyncRoom)
  ));
};

/** Purely a UI question: are the buttons mine to press? Never authorises a write. */
export const iNeedToDecide = ({ gameState, myUid }) => {
  if (!gameState || gameState.meta.phase === PHASES.FINISHED || gameState.meta.roundOver) return false;
  const me = gameState.players.find((p) => p.id === myUid);
  if (!me) return false;
  if (gameState.meta.phase === PHASES.BUYING) return gameState.meta.turnSeat === me.seat;
  return me.pick === null;
};

/**
 * Which bot temperament plays a given seat. Real bot seats keep the one they
 * were dealt; an absent human's stand-in plays steady.
 */
export const personalityForSeat = (player) => (
  player.isBot ? personalityFor(player.botIndex ?? 0) : 'steady'
);

/**
 * This game's async adapter (asyncPlay.js): who the game waits on now.
 * A bot's seat never waits on the bot; it waits on the lowest-seat human,
 * who drives the bots the moment they open the room.
 */
const lowestHuman = (g) => {
  const humans = (g?.players || []).filter((p) => !p.isBot);
  return humans.length ? [humans.reduce((best, p) => (p.seat < best.seat ? p : best)).id] : [];
};
export const asyncAdapter = {
  waitingOnOf: (g) => {
    if (!g || g.meta.phase === PHASES.FINISHED) return [];
    if (g.meta.roundOver) return lowestHuman(g);
    if (g.meta.phase === PHASES.BUYING) {
      const turnPlayer = g.players[g.meta.turnSeat];
      return turnPlayer && !turnPlayer.isBot ? [turnPlayer.id] : lowestHuman(g);
    }
    const picking = g.players.filter((p) => p.pick === null && !p.isBot).map((p) => p.id);
    return picking.length ? picking : (g.players.some((p) => p.pick === null) ? lowestHuman(g) : []);
  },
  isOver: (g) => g?.meta?.phase === PHASES.FINISHED,
};

/** After a move in an async room: who the game waits on now (asyncPlay.js). */
export const recordWaiting = async ({ gameId, gameState, meta }) => {
  if (!db || !meta?.async || !gameState) return;
  await update(dbRef(db, `${GAMES}/${gameId}/meta`), waitingPatch(gameState, meta, asyncAdapter)).catch(() => {});
};

export const createRoom = async ({ user, tableSize = MIN_PLAYERS, async: asyncRoom = false }) => {
  if (!db) throw new Error('Online play needs Firebase configured (see .env.example).');
  if (!user) throw new Error('Sign in first.');

  const gameRef = push(dbRef(db, GAMES));
  const gameId = gameRef.key;

  let code;
  let attempts = 0;
  do {
    code = generateRoomCode(attempts);
    attempts += 1;
    // eslint-disable-next-line no-await-in-loop
  } while (attempts < 12 && (await get(dbRef(db, `${CODES}/${code}`))).exists());

  const meta = {
    hostUid: user.uid,
    status: 'lobby',
    roomCode: code,
    async: asyncRoom,
    createdAt: new Date().toISOString(),
    lastActiveAt: Date.now(),
    tableSize: clampTableSize(tableSize),
    playerSlots: [{
      uid: user.uid,
      displayName: user.displayName || 'Host',
      seatIndex: 0,
    }],
  };

  await set(dbRef(db, `${GAMES}/${gameId}/meta`), meta);
  await set(dbRef(db, `${CODES}/${code}`), gameId);
  await set(dbRef(db, `${USER_GAMES}/${user.uid}/${gameId}`), { role: 'host', joinedAt: meta.createdAt });

  return { gameId, code };
};

export const joinRoom = async ({ user, code }) => {
  if (!db) throw new Error('Online play needs Firebase configured (see .env.example).');
  if (!user) throw new Error('Sign in first.');

  const normalized = normalizeRoomCode(code);
  const codeSnap = await get(dbRef(db, `${CODES}/${normalized}`));
  if (!codeSnap.exists()) throw new Error(`No game found for code "${normalized}".`);
  const gameId = codeSnap.val();

  const metaSnap = await get(dbRef(db, `${GAMES}/${gameId}/meta`));
  if (!metaSnap.exists()) throw new Error('That game no longer exists.');
  const meta = metaSnap.val();

  const slots = meta.playerSlots || [];
  const alreadyIn = slots.some((s) => s.uid === user.uid);

  // Rejoining a game you already have a seat in is always allowed, even
  // mid-game - that IS the path back for someone who dropped out.
  if (!alreadyIn) {
    if (meta.status !== 'lobby') throw new Error('That game has already started.');
    if (slots.length >= (meta.tableSize || MAX_PLAYERS)) throw new Error('That game is full.');
    slots.push({
      uid: user.uid,
      displayName: user.displayName || 'Guest',
      seatIndex: slots.length,
    });
    await update(dbRef(db, `${GAMES}/${gameId}/meta`), { playerSlots: slots, lastActiveAt: Date.now() });
    await set(dbRef(db, `${USER_GAMES}/${user.uid}/${gameId}`), { role: 'guest', joinedAt: new Date().toISOString() });
  }

  return { gameId };
};

export const setTableSize = async ({ user, gameId, meta, tableSize }) => {
  if (!db) throw new Error('Online play needs Firebase configured.');
  if (meta.hostUid !== user?.uid) throw new Error('Only the host can change the table size.');
  if (meta.status !== 'lobby') throw new Error('The game has already started.');
  const seated = (meta.playerSlots || []).length;
  await update(dbRef(db, `${GAMES}/${gameId}/meta`), {
    tableSize: clampTableSize(Math.max(tableSize, seated)),
  });
};

export const leaveLobby = async ({ user, gameId }) => {
  if (!db || !user || !gameId) return;
  const metaSnap = await get(dbRef(db, `${GAMES}/${gameId}/meta`));
  if (!metaSnap.exists()) return;
  const meta = metaSnap.val();
  if (meta.status !== 'lobby') return; // mid-game seats are kept, not vacated
  const slots = (meta.playerSlots || [])
    .filter((s) => s.uid !== user.uid)
    .map((s, i) => ({ ...s, seatIndex: i }));
  await update(dbRef(db, `${GAMES}/${gameId}/meta`), { playerSlots: slots });
  await remove(dbRef(db, `${GAMES}/${gameId}/presence/${user.uid}`));
};

/**
 * Host-only: turns the lobby's seats into a real game. Bots top the table up
 * to the host's chosen size, so two friends can start without waiting for a
 * third - the rulebook's minimum is three.
 */
export const startRoom = async ({ user, gameId, meta }) => {
  if (!db) throw new Error('Online play needs Firebase configured.');
  if (meta.hostUid !== user?.uid) throw new Error('Only the host can start the game.');

  const seats = [...(meta.playerSlots || [])].sort((a, b) => a.seatIndex - b.seatIndex);
  if (seats.length === 0) throw new Error('Nobody is in the room.');

  const humans = seats.map((s) => ({ id: s.uid, name: s.displayName, isBot: false }));
  const total = clampTableSize(Math.max(meta.tableSize || MIN_PLAYERS, humans.length));
  const bots = Array.from({ length: Math.max(0, total - humans.length) }, (_, i) => ({
    id: `bot${i}`,
    name: PERSONALITIES[personalityFor(i)].label,
    isBot: true,
    botIndex: i,
  }));

  // Host-authoritative seed, so every client renders the identical game from
  // the snapshot it receives rather than each shuffling its own.
  const seed = Math.floor(Math.random() * 2 ** 31);
  const gameState = createGameState({ players: [...humans, ...bots], seed });

  await set(dbRef(db, `${GAMES}/${gameId}/state`), encode(gameState));
  await update(dbRef(db, `${GAMES}/${gameId}/meta`), {
    status: 'in-progress',
    lastActiveAt: Date.now(),
    // An async room says who it waits on from the first turn (asyncPlay.js).
    ...(meta.async ? waitingPatch(gameState, meta, asyncAdapter) : {}),
  });
  return gameState;
};

/**
 * Applies one action to the shared state, inside a transaction.
 *
 * NO OPTIMISTIC-CONCURRENCY TOKEN, following incan-gold rather than
 * no-thanks, because the selling phase has everybody writing to the same
 * node in the same second - a seq check would keep the first tap and
 * silently drop the rest, leaving players stuck on "thinking…". The
 * transaction handler already re-runs against what is actually stored, and
 * every engine action guards itself against fresh data: BID and PASS do
 * nothing for a seat that isn't the turn seat, PICK nothing for a card not
 * in that hand, RESOLVE_SALE nothing until everyone has picked, START_ROUND
 * nothing unless a round is over. A no-op leaves `seq` unmoved, which aborts
 * the write - the protection applied at the only point that can actually
 * tell. (The buying phase is turn-based and wouldn't need this, but the
 * stricter phase sets the strategy for both.)
 *
 * `apply` returns null (or an unchanged state) to abandon.
 */
export const commitAction = async ({ gameId, apply }) => {
  if (!db) throw new Error('Online play needs Firebase configured.');
  const result = await runTransaction(dbRef(db, `${GAMES}/${gameId}/state`), (raw) => {
    const current = decode(raw);
    if (!current) return raw;
    const next = apply(current);
    // Returning undefined aborts and does NOT retry, which is what we want
    // for an action that turned out to be a no-op against fresh data.
    if (!next || seqOf(next) === seqOf(current)) return undefined;
    return encode(next);
  });
  return { committed: result.committed, state: decode(result.snapshot.val()) };
};

/**
 * Deletes the caller's own dead rooms. Self-service rather than a global
 * sweep, because that is what the security rules permit - same shape and
 * reasoning as every sibling.
 */
export const sweepMyRooms = async ({ user, now = Date.now(), database = db }) => {
  if (!database || !user) return { deleted: 0, pruned: 0 };
  let deleted = 0;
  let pruned = 0;

  const mine = await get(dbRef(database, `${USER_GAMES}/${user.uid}`));
  if (!mine.exists()) return { deleted, pruned };

  for (const [gameId, entry] of Object.entries(mine.val() || {})) {
    try {
      // eslint-disable-next-line no-await-in-loop -- serial keeps one failure
      // from cancelling the rest.
      const metaSnap = await get(dbRef(database, `${GAMES}/${gameId}/meta`));
      if (!metaSnap.exists()) {
        // eslint-disable-next-line no-await-in-loop
        await remove(dbRef(database, `${USER_GAMES}/${user.uid}/${gameId}`));
        pruned += 1;
        continue;
      }
      if (entry?.role !== 'host' && metaSnap.val().hostUid !== user.uid) continue;

      // eslint-disable-next-line no-await-in-loop
      const presenceSnap = await get(dbRef(database, `${GAMES}/${gameId}/presence`));
      const meta = metaSnap.val();
      if (!isDeadRoom(meta, presenceSnap.val() || {}, now)) continue;

      // Room first: a code may only be released once it points at nothing.
      // eslint-disable-next-line no-await-in-loop
      await remove(dbRef(database, `${GAMES}/${gameId}`));
      if (meta.roomCode) {
        // eslint-disable-next-line no-await-in-loop
        await remove(dbRef(database, `${CODES}/${meta.roomCode}`)).catch(() => {});
      }
      // eslint-disable-next-line no-await-in-loop
      await remove(dbRef(database, `${USER_GAMES}/${user.uid}/${gameId}`)).catch(() => {});
      deleted += 1;
    } catch {
      // A permission change, a dropped connection - none of it should stop
      // the rest of the sweep.
    }
  }
  return { deleted, pruned };
};

export const bindRoom = ({ gameId, user, onMeta, onState, onPresence }) => {
  if (!db) throw new Error('Online play needs Firebase configured.');

  const metaRef = dbRef(db, `${GAMES}/${gameId}/meta`);
  const stateRef = dbRef(db, `${GAMES}/${gameId}/state`);
  const presenceRef = dbRef(db, `${GAMES}/${gameId}/presence`);

  const offMeta = onValue(metaRef, (snap) => onMeta(snap.val()));
  const offState = onValue(stateRef, (snap) => onState(decode(snap.val())));
  const offPresence = onValue(presenceRef, (snap) => onPresence(snap.val() || {}));

  // Let Firebase clear our presence the instant the connection drops - a
  // closed tab, a dead network, a crashed browser. Far more reliable than an
  // explicit "I'm leaving" write, which a force-quit never gets to send.
  let myPresenceRef = null;
  if (user) {
    myPresenceRef = dbRef(db, `${GAMES}/${gameId}/presence/${user.uid}`);
    onDisconnect(myPresenceRef).remove();
    set(myPresenceRef, true);
    update(dbRef(db, `${GAMES}/${gameId}/meta`), { lastActiveAt: Date.now() }).catch(() => {});
  }

  return () => {
    offMeta();
    offState();
    offPresence();
    if (myPresenceRef) remove(myPresenceRef);
  };
};
